import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  BackwardTraceResponse,
  BackwardTraceRunResponse,
  ForwardTraceResponse,
  TraceSearchMode,
  TraceSearchResponse,
} from '../types/trace';

export function useTraceSearch(
  query: string,
  mode: TraceSearchMode,
  enabled: boolean,
) {
  return useQuery<TraceSearchResponse>({
    queryKey: ['trace', 'search', query, mode],
    queryFn: () => {
      const params = new URLSearchParams({ q: query, mode });
      return apiFetch<TraceSearchResponse>(`/api/admin/trace/search?${params}`);
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 0,
  });
}

export function useForwardTrace(signalId: number | null) {
  return useQuery<ForwardTraceResponse>({
    queryKey: ['trace', 'forward', signalId],
    queryFn: () => apiFetch<ForwardTraceResponse>(
      `/api/admin/trace/by-signal/${signalId}`,
    ),
    enabled: signalId !== null,
    staleTime: 0,
  });
}

export function useBackwardTrace(
  dealKind: 'candidate' | 'affinity_opp' | null,
  dealKey: string | null,
  field: string | null,
) {
  return useQuery<BackwardTraceResponse>({
    queryKey: ['trace', 'backward', dealKind, dealKey, field],
    queryFn: () => {
      const params = new URLSearchParams();
      if (field) params.set('field', field);
      const qs = params.toString();
      return apiFetch<BackwardTraceResponse>(
        `/api/admin/trace/by-deal/${dealKind}/${dealKey}${qs ? `?${qs}` : ''}`,
      );
    },
    enabled: dealKind !== null && dealKey !== null,
    staleTime: 0,
  });
}

export function useBackwardTraceRun(
  dealKind: 'candidate' | 'affinity_opp' | null,
  dealKey: string | null,
  runId: number | null,
) {
  return useQuery<BackwardTraceRunResponse>({
    queryKey: ['trace', 'backward-run', dealKind, dealKey, runId],
    queryFn: () => apiFetch<BackwardTraceRunResponse>(
      `/api/admin/trace/by-deal/${dealKind}/${dealKey}/run/${runId}`,
    ),
    enabled: dealKind !== null && dealKey !== null && runId !== null,
    staleTime: 0,
  });
}
